import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Use cookies for auth
axios.defaults.withCredentials = true;

// Icon for a diet-plan file based on its filename extension.
function filePlanIcon(plan) {
  const name = (plan?.file_name || plan?.file_url || '').toLowerCase();
  if (/\.(pdf)$/.test(name)) return '📄';
  if (/\.(docx?|odt|rtf)$/.test(name)) return '📝';
  if (/\.(xlsx?|csv|ods)$/.test(name)) return '📊';
  if (/\.(png|jpe?g|webp|gif|heic|heif|tiff?|bmp)$/.test(name)) return '🖼️';
  return '📎';
}

// ─── Page Frame ───────────────────────────────────────────────────────────────
function PageFrame({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F7F3EE', fontFamily: "'Instrument Sans', sans-serif" }}>
      <header style={{ background: '#0F4C5C', color: '#fff', padding: '16px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <a href="/home.html" style={{ fontFamily: "'Great Vibes', cursive", fontSize: '1.6rem', color: '#fff', textDecoration: 'none' }}>Anjum Sohail</a>
        <div role="navigation" style={{ display: 'flex', gap: 16, fontSize: '0.9rem' }}>
          <a href="/home.html" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none' }}>Home</a>
          <a href="/recipes.html" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none' }}>Recipes</a>
          <a href="/shop.html" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none' }}>Shop</a>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

// ─── Login (Google + Email OTP) ──────────────────────────────────────────────
function ClientLogin() {
  // step: 'options' | 'email' | 'code' | 'success'
  const [step, setStep] = useState('options');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [devCode, setDevCode] = useState('');
  const [resentAt, setResentAt] = useState(0);

  const sendCode = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/email-otp/send`, { email });
      setStep('code');
      setCode('');
      setDevCode(r.data?.dev_code || '');
      setResentAt(Date.now());
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not send the code. Please check the email address.');
    } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    setErr(''); setBusy(true);
    try {
      await axios.post(`${API}/auth/email-otp/verify`, { email, code: code.trim() });
      window.location.href = '/dashboard';
    } catch (e) {
      setErr(e.response?.data?.detail || 'Invalid code. Please try again.');
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ─── Step: choose method (default) ────────────────────────────────────────
  const renderOptions = () => (
    <>
      <button data-testid="google-login-btn" onClick={googleLogin} style={btnGoogleTeal}>
        <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 10 }}>
          <path fill="#fff" d="M17.64 9.2a10.34 10.34 0 0 0-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z"/>
          <path fill="#fff" opacity="0.85" d="M9 18a8.6 8.6 0 0 0 5.96-2.18l-2.92-2.26A5.4 5.4 0 0 1 9 14.34a5.4 5.4 0 0 1-5.08-3.74H.92v2.34A9 9 0 0 0 9 18z"/>
          <path fill="#fff" opacity="0.7" d="M3.92 10.6a5.4 5.4 0 0 1 0-3.2V5.06H.92a9 9 0 0 0 0 7.88l3-2.34z"/>
          <path fill="#fff" opacity="0.85" d="M9 3.58a4.86 4.86 0 0 1 3.44 1.34L15.02 2.34A8.66 8.66 0 0 0 9 0a9 9 0 0 0-8.08 5.06l3 2.34A5.4 5.4 0 0 1 9 3.58z"/>
        </svg>
        Continue with Google
      </button>

      <div style={dividerWrap}>
        <div style={dividerLine} />
        <span style={dividerText}>OR</span>
        <div style={dividerLine} />
      </div>

      <button data-testid="show-email-login-btn" onClick={() => { setStep('email'); setErr(''); }} style={btnEmailOutline}>
        Login with Email
      </button>
      <p style={{ fontSize: '0.78rem', color: '#5C7079', marginTop: 14, textAlign: 'center', lineHeight: 1.5 }}>
        We will email you a one-time code. No password needed.
      </p>
    </>
  );

  // ─── Step: enter email ────────────────────────────────────────────────────
  const renderEmail = () => (
    <>
      <button data-testid="back-to-options-btn" onClick={() => { setStep('options'); setErr(''); }} style={linkBack}>← Back</button>
      <label style={fieldLabel}>Email address</label>
      <input
        data-testid="login-email-input"
        type="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        onKeyDown={(e) => { if (e.key === 'Enter' && email && !busy) sendCode(); }}
        style={fieldInput}
      />
      <button data-testid="send-code-btn" onClick={sendCode} disabled={busy || !email} style={{ ...btnTeal, opacity: (busy || !email) ? 0.6 : 1, cursor: (busy || !email) ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Sending code...' : 'Send Code'}
      </button>
      <p style={{ fontSize: '0.78rem', color: '#5C7079', marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
        We will send a 6-digit code to your inbox.
      </p>
    </>
  );

  // ─── Step: enter 6-digit code ─────────────────────────────────────────────
  const renderCode = () => (
    <>
      <button data-testid="back-to-email-btn" onClick={() => { setStep('email'); setErr(''); setDevCode(''); }} style={linkBack}>← Use a different email</button>
      <p style={{ fontSize: '0.92rem', color: '#1A2E35', marginBottom: 14, textAlign: 'center', lineHeight: 1.55 }}>
        We sent a 6-digit code to<br/><strong style={{ color: '#0F4C5C' }}>{email}</strong>
      </p>
      <label style={fieldLabel}>Enter the 6-digit code</label>
      <input
        data-testid="login-code-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
        placeholder="000000"
        onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6 && !busy) verifyCode(); }}
        style={{ ...fieldInput, letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.6rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#0F4C5C' }}
      />
      <button data-testid="verify-code-btn" onClick={verifyCode} disabled={busy || code.length !== 6} style={{ ...btnTeal, opacity: (busy || code.length !== 6) ? 0.6 : 1, cursor: (busy || code.length !== 6) ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Verifying...' : 'Verify & Sign In'}
      </button>
      {devCode && (
        <div data-testid="dev-otp-code" style={devBanner}>
          <strong>Dev mode:</strong>&nbsp;Email sending isn't configured yet.<br/>
          Your code is: <strong style={{ color: '#0F4C5C', fontSize: '1.05rem' }}>{devCode}</strong>
        </div>
      )}
      <button data-testid="resend-code-btn" onClick={sendCode} disabled={busy || Date.now() - resentAt < 30000} style={{ background: 'none', border: 'none', color: '#0F4C5C', cursor: 'pointer', marginTop: 14, fontSize: '0.82rem', textDecoration: 'underline', width: '100%' }}>
        {Date.now() - resentAt < 30000 ? 'Resend code in 30s' : "Didn't receive it? Resend code"}
      </button>
      <p style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: 10, textAlign: 'center', lineHeight: 1.5 }}>
        The code expires in 10 minutes. Don't see it? Check your spam folder.
      </p>
    </>
  );

  return (
    <PageFrame>
      <div data-testid="client-login-page" style={{ display: 'flex', justifyContent: 'center', padding: '60px 20px' }}>
        <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20, padding: 36, boxShadow: '0 24px 64px rgba(15,76,92,0.12)', border: '1px solid #E2EDF1' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontFamily: "'Great Vibes', cursive", fontSize: '2.2rem', color: '#0F4C5C' }}>Anjum Sohail</div>
            <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.4rem', color: '#1A2E35', marginTop: 4 }}>Client Login</h1>
            <p style={{ fontSize: '0.85rem', color: '#5C7079', marginTop: 6 }}>Sign in to access your plan, diet plans and orders.</p>
            <div style={{ width: 56, height: 3, background: '#E07A5F', margin: '14px auto 0', borderRadius: 2 }} />
          </div>

          {step === 'options' && renderOptions()}
          {step === 'email' && renderEmail()}
          {step === 'code' && renderCode()}

          {err && <div data-testid="login-error" style={{ background: '#FCE7E7', color: '#B91C1C', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem', marginTop: 12 }}>{err}</div>}
        </div>
      </div>
    </PageFrame>
  );
}

const btnTeal = { width: '100%', padding: '13px 16px', background: '#0F4C5C', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' };
const btnGoogleTeal = { width: '100%', padding: '13px 16px', background: '#0F4C5C', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', letterSpacing: '0.01em' };
const btnEmailOutline = { width: '100%', padding: '13px 16px', background: '#fff', color: '#0F4C5C', border: '1.5px solid #0F4C5C', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' };
const dividerWrap = { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' };
const dividerLine = { flex: 1, height: 1, background: '#E2EDF1' };
const dividerText = { fontSize: '0.74rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600 };
const fieldLabel = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#1A2E35', marginBottom: 6 };
const fieldInput = { width: '100%', padding: '12px 14px', border: '1px solid #E2EDF1', borderRadius: 10, fontSize: '0.95rem', marginBottom: 12, fontFamily: 'inherit', boxSizing: 'border-box' };
const linkBack = { background: 'none', border: 'none', color: '#0F4C5C', cursor: 'pointer', fontSize: '0.82rem', padding: 0, marginBottom: 14, textAlign: 'left' };
const devBanner = { marginTop: 14, background: '#FFF7E5', border: '1px dashed #E0A800', borderRadius: 8, padding: '12px 14px', fontSize: '0.78rem', color: '#7C5500', textAlign: 'center', lineHeight: 1.55 };
const btnGoogle = btnGoogleTeal; // legacy export kept (referenced elsewhere)

// ─── Auth Callback (Google) ───────────────────────────────────────────────────
function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate('/login'); return; }
    const sessionId = m[1];
    axios.post(`${API}/auth/google/session`, { session_id: sessionId })
      .then(() => {
        // Strip hash and go to dashboard
        window.history.replaceState({}, document.title, '/dashboard');
        navigate('/dashboard');
      })
      .catch((e) => {
        setErr(e.response?.data?.detail || 'Auth failed');
        setTimeout(() => navigate('/login'), 2200);
      });
  }, [navigate]);

  return (
    <PageFrame>
      <div style={{ padding: 80, textAlign: 'center', color: '#5C7079' }}>
        {err ? <div data-testid="auth-callback-error" style={{ color: '#B91C1C' }}>{err} — redirecting…</div> : 'Signing you in…'}
      </div>
    </PageFrame>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dietReview, setDietReview] = useState({ has_purchased: false, has_report: false });
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clients/me`);
      setMe(r.data);
      if (!r.data.blocked) {
        const [p, o, dr] = await Promise.all([
          axios.get(`${API}/clients/me/diet-plans`).catch(() => ({ data: [] })),
          axios.get(`${API}/clients/me/orders`).catch(() => ({ data: [] })),
          axios.get(`${API}/clients/me/diet-review`).catch(() => ({ data: { has_purchased: false, has_report: false } })),
        ]);
        setPlans(p.data || []);
        setOrders(o.data || []);
        setDietReview(dr.data || { has_purchased: false, has_report: false });
      }
    } catch (e) {
      navigate('/login');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return; // AuthCallback handles
    load();
  }, [load]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {}
    navigate('/login');
  };

  if (loading) return <PageFrame><div style={{ padding: 80, textAlign: 'center', color: '#5C7079' }}>Loading…</div></PageFrame>;
  if (!me) return null;

  const statusColor = me.effective_status === 'active' ? '#10B981' : (me.effective_status === 'paused' ? '#F59E0B' : '#EF4444');

  const currentPlan = plans.find(p => p.is_current);
  const historicalPlans = plans.filter(p => !p.is_current);

  return (
    <PageFrame>
      <div data-testid="client-dashboard" style={{ maxWidth: 980, margin: '0 auto', padding: '32px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.8rem', color: '#1A2E35', margin: 0 }}>Welcome, {me.name}</h1>
            <p style={{ color: '#5C7079', fontSize: '0.9rem', marginTop: 4 }}>Client ID: <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 6 }}>{me.client_id}</code></p>
          </div>
          <button data-testid="dashboard-logout" onClick={logout} style={{ background: '#fff', border: '1px solid #E2EDF1', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Logout</button>
        </div>

        {/* Plan Status Card */}
        <div data-testid="plan-status-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #E2EDF1', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#5C7079', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Your Plan</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.4rem', color: '#1A2E35', marginTop: 4 }}>
                {{'one-time': 'One-Time Consultation', '1-month': '1 Month', '3-months': '3 Months', '6-months': '6 Months', 'annual': 'Annual'}[me.plan_type] || me.plan_type}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#5C7079', marginTop: 8 }}>
                <strong>Started:</strong> {me.plan_start || '—'} &nbsp;·&nbsp; <strong>Expires:</strong> {me.plan_expiry || '—'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span data-testid="plan-status-badge" style={{ display: 'inline-block', background: statusColor + '22', color: statusColor, padding: '6px 14px', borderRadius: 999, fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {me.effective_status}
              </span>
              {me.days_remaining != null && (
                <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#5C7079' }}>
                  <strong style={{ color: '#0F4C5C', fontSize: '1.1rem' }}>{me.days_remaining}</strong> days remaining
                </div>
              )}
            </div>
          </div>
          {me.blocked && (
            <div data-testid="blocked-message" style={{ marginTop: 16, background: '#FEF3C7', color: '#92400E', padding: '12px 16px', borderRadius: 10, fontSize: '0.9rem' }}>
              {me.reason}
            </div>
          )}
        </div>

        {!me.blocked && dietReview.has_purchased && (
          <div data-testid="diet-review-report" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #E2EDF1', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: '#E07A5F', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>Personalised Report</div>
                <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.25rem', color: '#1A2E35', margin: '4px 0 0' }}>Your Diet Review Report</h2>
              </div>
              {dietReview.has_report && dietReview.report_sent_at && (
                <div style={{ fontSize: '0.75rem', color: '#5C7079', textAlign: 'right' }}>
                  Sent on<br/><strong style={{ color: '#0F4C5C' }}>{new Date(dietReview.report_sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                </div>
              )}
            </div>
            {dietReview.has_report ? (
              <>
                {[
                  { k: 'section_1_health_snapshot', t: '1. Health Snapshot' },
                  { k: 'section_2_diet_gaps', t: '2. What Your Diet is Missing' },
                  { k: 'section_3_recommendations', t: '3. Personalised Recommendations' },
                  { k: 'section_4_meal_plan', t: '4. 3-Day Sample Meal Plan' },
                  { k: 'section_5_next_steps', t: "5. Next Steps & Anjum's Note" },
                ].filter(s => (dietReview.sections || {})[s.k]?.trim()).map(s => (
                  <div key={s.k} data-testid={`portal-${s.k}`} style={{ marginBottom: 18 }}>
                    <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: '1rem', color: '#0F4C5C', margin: '0 0 6px', paddingBottom: 5, borderBottom: '2px solid #E07A5F' }}>{s.t}</h3>
                    <div style={{ fontSize: '.9rem', color: '#1A2E35', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{(dietReview.sections || {})[s.k]}</div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: '1px solid #E2EDF1' }}>
                  <a data-testid="download-report-pdf" href={`${API}/clients/me/diet-review/pdf`} style={{ background: '#0F4C5C', color: '#fff', padding: '10px 20px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>📄 Download as PDF</a>
                  <a data-testid="report-whatsapp" href="https://wa.me/919187542262?text=Hi%20Anjum%2C%20I%20have%20a%20question%20about%20my%20diet%20review%20report." target="_blank" rel="noopener noreferrer" style={{ background: '#fff', color: '#0F4C5C', padding: '10px 20px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none', border: '1.5px solid #0F4C5C', display: 'inline-flex', alignItems: 'center', gap: 6 }}>💬 Contact Anjum on WhatsApp</a>
                </div>
              </>
            ) : (
              <div style={{ background: '#FEF3C7', color: '#78350F', padding: '14px 18px', borderRadius: 10, fontSize: '.9rem', lineHeight: 1.6 }}>
                Your personalised report is being prepared by Anjum. You will receive it by email within 48 hours.
              </div>
            )}
          </div>
        )}

        {!me.blocked && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
            {/* Diet Plans */}
            <div data-testid="diet-plans-section" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #E2EDF1' }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.15rem', color: '#1A2E35', marginTop: 0 }}>Your Diet Plans</h2>
              {currentPlan ? (
                <div style={{ background: '#F7F3EE', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: '0.72rem', color: '#0F4C5C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current · v{currentPlan.version}</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1A2E35', marginTop: 4 }}>{currentPlan.title}</div>
                  {currentPlan.type === 'file' && currentPlan.file_url && (
                    <a href={currentPlan.file_url} target="_blank" rel="noopener noreferrer" data-testid="current-plan-download" style={{ display: 'inline-block', marginTop: 8, background: '#0F4C5C', color: '#fff', padding: '8px 14px', borderRadius: 8, textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600 }}>{filePlanIcon(currentPlan)} {currentPlan.file_name ? `Open · ${currentPlan.file_name}` : 'Open / Download'}</a>
                  )}
                  {currentPlan.type === 'rich' && currentPlan.content && (
                    <div style={{ marginTop: 10, fontSize: '0.88rem', color: '#1A2E35', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 240, overflowY: 'auto' }}>{currentPlan.content}</div>
                  )}
                </div>
              ) : (
                <p style={{ color: '#5C7079', fontSize: '0.88rem' }}>No diet plan yet — Anjum will share one shortly.</p>
              )}
              {historicalPlans.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: '0.82rem', color: '#5C7079', cursor: 'pointer', fontWeight: 600 }}>Past plans ({historicalPlans.length})</summary>
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {historicalPlans.map(p => (
                      <div key={p.id} onClick={() => setActivePlan(p)} style={{ padding: '10px 12px', background: '#F7F3EE', borderRadius: 8, cursor: 'pointer' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>v{p.version} · {p.title}</div>
                        <div style={{ fontSize: '0.72rem', color: '#5C7079' }}>{p.created_at?.slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Orders */}
            <div data-testid="orders-section" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #E2EDF1' }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.15rem', color: '#1A2E35', marginTop: 0 }}>Your Orders</h2>
              {orders.length === 0 ? (
                <p style={{ color: '#5C7079', fontSize: '0.88rem' }}>No orders yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {orders.map(o => (
                    <div key={o.order_id} style={{ padding: 12, background: '#F7F3EE', borderRadius: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <strong style={{ fontSize: '0.92rem', color: '#1A2E35' }}>{o.product_name}</strong>
                        <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 999, background: o.payment_status === 'paid' ? '#D1FAE5' : '#FEF3C7', color: o.payment_status === 'paid' ? '#065F46' : '#92400E', fontWeight: 700, textTransform: 'uppercase' }}>{o.order_status}</span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#5C7079', marginTop: 4 }}>
                        ₹{o.amount} · {o.order_id} · {o.created_at?.slice(0, 10)}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <a
                          href={`/track-order.html?id=${encodeURIComponent(o.order_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="track-order-link"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', fontWeight: 700, color: '#0F4C5C', background: '#fff', border: '1.5px solid #0F4C5C', padding: '5px 12px', borderRadius: 999, textDecoration: 'none' }}
                        >
                          📦 Track Order
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activePlan && (
          <div onClick={() => setActivePlan(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontFamily: "'Fraunces', serif", margin: 0, color: '#1A2E35' }}>v{activePlan.version} · {activePlan.title}</h3>
                <button onClick={() => setActivePlan(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#5C7079' }}>×</button>
              </div>
              {activePlan.type === 'file' && activePlan.file_url && (
                <a href={activePlan.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: '#0F4C5C', color: '#fff', padding: '10px 16px', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>{filePlanIcon(activePlan)} {activePlan.file_name ? `Open · ${activePlan.file_name}` : 'Open / Download'}</a>
              )}
              {activePlan.type === 'rich' && (
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.92rem', lineHeight: 1.6, color: '#1A2E35' }}>{activePlan.content}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageFrame>
  );
}

// ─── Main router ──────────────────────────────────────────────────────────────
export default function ClientPortal() {
  const location = useLocation();
  // Process Google session_id callback fragment FIRST (synchronously during render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  // Decide what to render based on pathname (avoids nested Routes warnings)
  if (location.pathname.startsWith('/dashboard')) {
    return <Dashboard />;
  }
  return <ClientLogin />;
}
